import { PROJECTION } from 'constants/ApiConstants';
import { ENTITY_TYPE } from 'modules/app/constants';
import { putAndWait } from 'modules/app/sagas';
import { uniq } from 'utils/ObjectUtils';

import { INSPECTION_FOREIGN_ACTION, INSPECTION_FOREIGN_KEY } from '../constants/inspections';
import { InspectionStatusEnum } from '../types/inspections';

/**
 * Transforms a loaded entity (audit, scan, work order) into a normalized Inspection format.
 *
 * This function creates a standardized inspection object that can be used across the application,
 * regardless of the original entity type (audit, tire scan, body scan, or work order).
 *
 * IMPORTANT BEHAVIORS:
 * - Sets `imported: true` because these entities are being loaded specifically because they are
 *   already referenced (imported) in the intervention's checklists. This differs from the
 *   /inspections endpoint which returns the actual imported status from the backend.
 * - The `createdOn` field is preserved as-is from the original entity. This timestamp is used for
 *   sorting inspections chronologically (most recent first) within each inspection type group.
 * - The `reportUrl` field (if present) allows direct PDF viewing for external audits and scans.
 *
 * @param {import("modules/audits/types/stored-audit").StoredAudit | import('modules/tireScan/types').TireScan | import("../types/intervention").WorkOrder | Object} entity - The source entity to transform
 * @param {import('../constants/inspections').InspectionTypeEnum} type - The inspection type (AUDIT, TIRE_SCAN, BODY_SCAN, EXTERNAL_AUDIT)
 * @param {string} [entityIdKey='id'] - The key to use for the ID field (default: 'id', can be foreign key like 'originAuditId')
 * @returns {import('../types/inspections').Inspection} Normalized inspection object with type, imported flag, id, dates, status, and reference
 */
export const castEntityToInspection = (entity, type, entityIdKey = 'id') => ({
  type,
  imported: true,
  id: entity[entityIdKey],
  reportUrl: entity.reportUrl,
  createdOn: entity.createdOn, // Used for chronological sorting (desc) within each type group
  status: entity.status,
  reference: entity.name ?? entity.reference ?? entity.registration ?? '',
});

/**
 * Identifies inspections that are referenced in checklists but not yet loaded.
 *
 * This function compares the intervention's checklists against already-loaded inspections to determine
 * which inspections need to be fetched. It groups missing inspections by type for parallel loading.
 *
 * NOTE: The returned inspections are placeholders with `imported: true` and will be replaced
 * by full entities when loaded via `getMissingInspectionRequests`.
 *
 * @param {import('../types/projections/detailed-intervention').DetailedIntervention} intervention - The intervention with checklists to analyze
 * @param {Array<import('../types/inspections').Inspection>} inspections - Already loaded inspections from /inspections endpoint
 * @returns {Record<import('../constants/inspections').InspectionTypeEnum, Array<import('../types/inspections').Inspection>>} Map of inspection type to missing inspections
 */
export const getMissingInspectionByInspectionType = (intervention, inspections) => {
  const { checklists = [] } = intervention;
  const inspectionMap = inspections.reduce((acc, { id, type }) => ({ ...acc, [type]: [...(acc[type] ?? []), id] }), {});

  return checklists.reduce((acc, checklist) => {
    const foreignInfo = Object.entries(INSPECTION_FOREIGN_KEY).find(
      ([, foreignChecklistKey]) => checklist[foreignChecklistKey] !== undefined
    );

    if (foreignInfo) {
      const [foreignType, foreignKey] = foreignInfo;
      const originId = checklist[foreignKey];
      const alreadyLoaded = inspectionMap[foreignType]?.includes(originId) ?? false;

      return alreadyLoaded
        ? acc
        : {
          ...acc,
          [foreignType]: uniq([
            ...(acc[foreignType] ?? []),
            castEntityToInspection(checklist, foreignType, foreignKey),
          ]),
        };
    }
    return acc;
  }, {});
};

/**
 * Creates saga effects to load missing inspections in parallel.
 *
 * This function generates the necessary Redux-Saga effects to fetch all missing inspections
 * identified by `getMissingInspectionByInspectionType`. Each inspection type is loaded via
 * its specific endpoint (audits API, tire scans API, etc.) with optimized parameters.
 *
 * LOADING STRATEGY:
 * - Parallel execution: All inspection types are loaded simultaneously using `yield all()`
 * - Specific IDs: Only requests entities that are actually missing (not already loaded)
 * - Contextual filtering: Uses vehicle registration and dealer ID for targeted queries
 * - Sorted results: Requests data sorted by `createdOn,desc` for consistent ordering
 *
 * SORTING NOTE: The loaded entities will be sorted by type (following INSPECTION_TYPE_ENUM order)
 * and then by createdOn (desc) within each type group in the calling saga.
 *
 * @param {import('../types/projections/detailed-intervention').DetailedIntervention} intervention - The intervention with vehicle and dealer context
 * @param {Array<import('../types/inspections').Inspection>} inspections - Already loaded inspections from /inspections endpoint
 * @returns {Record<import('../constants/inspections').InspectionTypeEnum, Effect>} Map of inspection type to saga effect (putAndWait)
 */
export const getMissingInspectionRequests = (intervention, inspections) => {
  const { id: interventionId, vehicleInfo = {} } = intervention;
  const inspectionsToLoad = getMissingInspectionByInspectionType(intervention, inspections);

  const baseParameters = {
    registration: vehicleInfo?.registration,
    dealerId: intervention.dealerId ?? intervention.dealer?.id,
    sort: 'createdOn,desc',
    paginationType: 'SLICE',
    projection: PROJECTION.PAGE,
  };

  return Object.entries(inspectionsToLoad).reduce((acc, [type, entities]) => {
    const fetchFn = INSPECTION_FOREIGN_ACTION[type];
    const ids = entities.map(({ id }) => id);

    return typeof fetchFn === 'function'
      ? {
        ...acc,
        [type]: putAndWait(
          INSPECTION_FOREIGN_ACTION[type](
            interventionId,
            { ...baseParameters, ids, size: ids.length },
            { source: 'intervention-details-inspections' }
          )
        ),
      }
      : acc;
  }, {});
};

/**
 * Determines if inspection can be imported based on access and status.
 *
 * IMPORTANT BEHAVIOR:
 *  - An inspection is "importable" event if it is already imported.
 *
 * @param {boolean} hasAccess
 * @param {import("../types/inspections").Inspection} inspection
 * @returns {boolean}
 */
export const isImportableInspection = (hasAccess, inspection) =>
  inspection?.type !== ENTITY_TYPE.AUDIT ||
  (hasAccess &&
    inspection.status !== InspectionStatusEnum.DRAFT &&
    inspection.status !== InspectionStatusEnum.TO_START);

/**
 * Determines if inspection can be imported (not yet imported) based on access, status and read-only.
 *
 * @param {boolean} hasAccess
 * @param {import("../types/inspections").Inspection} inspection
 * @param {boolean} isReadOnly
 * @returns {boolean}
 */
export const canImportInspection = (hasAccess, inspection, isReadOnly) => {
  const isAudit = inspection?.type === ENTITY_TYPE.AUDIT;
  const canImportAudit = hasAccess && inspection?.status === InspectionStatusEnum.OPEN;

  return Boolean(inspection) && !isReadOnly && !inspection?.imported && (!isAudit || canImportAudit);
};
